import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import TextField from "@/common/components/TextField";
import { strategy as unitForQuantityStrategy } from "@/common/constants/unitForQuantityOptions";
import { ChevronsRight } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import useProduct from "../../hooks/useProduct";
import { fiscalDocumentKindStrategy } from "@/common/constants/fiscalDocumentKindOptions.constant";
import GoBackButton from "@/common/components/GoBackButton";
import Select from "@/common/components/Select";
import { Mapper } from "@/common/services/mapper";
import { ProductData } from "@/features/products/types/product";
import { EquipmentCategoryData } from "@/features/equipmentModels/types/equipmentCategories";
import { tefProvidersStrategy } from "@/features/establishments/constants/tefModels.constant";

const Show = () => {
  const { id } = useParams();

  const {
    data: initialProduct,
    isPending: initialProductIsPending,
    error: initialProductError = {} as any,
    refetch: refetchProduct,
  } = useProduct(id || '');

  const disableUpdateButton = initialProductIsPending || initialProductError?.status === 403;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback="/products" />

          <p className="text-white-400 font-normal">
            Produtos
          </p>

          <ChevronsRight color="#ffffffb3" />

          <h2>
            {initialProduct?.display_name}
          </h2>
        </div>

        <Link
          to={disableUpdateButton ? '' : `/products/${id}/edit`}
          className={disableUpdateButton ? "cursor-default" : ''}
        >
          <Button operationKlassToCheckPermission="Admin::Products::Update" data-testid="access_products_show_button" type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" disabled={disableUpdateButton}>
            Editar
          </Button>
        </Link>
      </div>

      {initialProductIsPending ? (
        <Box>Carregando...</Box>
      ) : (
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-128px)]"
          statusCode={initialProductError?.status}
          refetchFunction={refetchProduct}
        >
          <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(100vh-128px)] overflow-y-auto">
            <p className="text-lg font-bold">
              {initialProduct?.display_name}
            </p>

            <TextField
              label="Categoria"
              data-testid="category_selector"
              disabled
              value={initialProduct?.category.display_name}
            />

            <div className="flex gap-2">
              <TextField
                label="Nome"
                data-testid="display_name_input"
                className="w-1/2"
                disabled
                value={initialProduct?.display_name}
              />

              <TextField
                label="Métrica"
                data-testid="unit_for_quantity_selector"
                className="w-1/2"
                disabled
                value={
                  unitForQuantityStrategy[initialProduct?.unit_for_quantity as keyof typeof unitForQuantityStrategy]
                }
              />
            </div>

            <Select
              label="Identificadores"
              dataTestid="product_tef_identifiers_selector"
              value={Mapper.mapToOptions<ProductData['product_tef_identifiers'][number]>({
                valueFieldName: 'id',
                labelFieldName: 'identifier',
              })(
                initialProduct?.product_tef_identifiers?.map((tefIdentifier) => ({
                  ...tefIdentifier,
                  identifier: `${
                    tefProvidersStrategy[tefIdentifier.tef_provider as keyof typeof tefProvidersStrategy]
                  }: ${tefIdentifier.identifier}`,
                })) || [],
              )}
              isMulti
              isDisabled
            />

            <Select
              label="Categorias de equipamento"
              dataTestid="equipment_category_selector"
              value={Mapper.mapToOptions<EquipmentCategoryData>({
                valueFieldName: 'id',
                labelFieldName: 'display_name',
              })(initialProduct?.equipment_categories || [])}
              isMulti
              isDisabled
            />

            <TextField
              label="Porte"
              data-testid="equipment_tier_input"
              disabled
              value={initialProduct?.equipment_tier?.display_name || ''}
            />

            <TextField
              label="Tipo de Documento Fiscal"
              data-testid="fiscal_document_kind_selector"
              disabled
              value={fiscalDocumentKindStrategy[initialProduct?.fiscal_document_kind as keyof typeof fiscalDocumentKindStrategy]}
            />
          </Box>
        </ErrorMessageWrapper>
      )}
    </div>
  );
};

export default Show;
